import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Gift,
  Copy,
  Check,
  X,
  Sparkles,
  Users,
  CreditCard,
  Zap
} from 'lucide-react';

const ReferralModal = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const getReferralLink = () => {
    const baseUrl = window.location.origin;
    const referralCode = user?.referral_code || user?.user_id?.slice(-8) || 'ref';
    return `${baseUrl}/login?ref=${referralCode}`;
  };

  const copyReferralLink = async () => {
    try {
      await navigator.clipboard.writeText(getReferralLink());
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
        >
          <X className="w-4 h-4 text-slate-500" />
        </button>

        {/* Header */}
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-8 rounded-t-3xl text-center border-b border-emerald-100">
          <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Gift className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2">
            Invite Friends, Earn Credits
          </h2>
          <p className="text-slate-600">
            Share your referral link and earn credits when your friends sign up or subscribe!
          </p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Rewards Grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-emerald-50 rounded-xl p-4 text-center border border-emerald-100">
              <Users className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-emerald-600">+100</p>
              <p className="text-xs text-slate-600">Per signup</p>
            </div>
            <div className="bg-emerald-50 rounded-xl p-4 text-center border border-emerald-100">
              <CreditCard className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-emerald-600">+2,000</p>
              <p className="text-xs text-slate-600">Pro subscription</p>
            </div>
            <div className="bg-emerald-50 rounded-xl p-4 text-center border border-emerald-100">
              <Zap className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-emerald-600">+5,000</p>
              <p className="text-xs text-slate-600">Scale subscription</p>
            </div>
            <div className="bg-emerald-50 rounded-xl p-4 text-center border border-emerald-100">
              <Sparkles className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-emerald-600">+10,000</p>
              <p className="text-xs text-slate-600">Business subscription</p>
            </div>
          </div>

          {/* Referral Link */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Your Referral Link
            </label>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-600 truncate font-mono">
                {getReferralLink()}
              </div>
              <Button 
                onClick={copyReferralLink}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-4"
              >
                {copied ? (
                  <Check className="w-5 h-5" />
                ) : (
                  <Copy className="w-5 h-5" />
                )}
              </Button>
            </div>
          </div>

          {/* How it works */}
          <div className="bg-slate-50 rounded-xl p-4">
            <h4 className="font-semibold text-slate-900 mb-3">How it works</h4>
            <ol className="space-y-2 text-sm text-slate-600">
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                <span>Share your unique referral link with friends</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                <span>They sign up using your link and get 100 free credits</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                <span>You earn 100 credits instantly + bonus when they subscribe</span>
              </li>
            </ol>
          </div>

          {/* Stats */}
          {user?.referral_stats && (
            <div className="border-t border-slate-200 pt-4">
              <h4 className="font-semibold text-slate-900 mb-3">Your Referral Stats</h4>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-2xl font-bold text-slate-900">{user.referral_stats.signups || 0}</p>
                  <p className="text-xs text-slate-500">Signups</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{user.referral_stats.purchases || 0}</p>
                  <p className="text-xs text-slate-500">Purchases</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-600">{(user.referral_stats.total_credits_earned || 0).toLocaleString()}</p>
                  <p className="text-xs text-slate-500">Credits Earned</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 pt-0">
          <Button 
            onClick={onClose}
            variant="outline"
            className="w-full"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReferralModal;
